import React, { useState, useEffect } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useInviteAuth } from './InviteAuthContext';
import { useAuth } from '../../context/AuthContext';
import { 
  Search, X, Mail, MessageSquare, Phone, Send, Sparkles, 
  ShieldCheck, HelpCircle, Check, ChevronDown, ChevronRight, MessageCircle, 
  Calendar, Users, BellRing, Award, Star, Compass, Play
} from 'lucide-react';
import './InviteLandingPage.css';
import weddingBg from '../indian_wedding_bg.png';

export const InviteLandingPage: React.FC = () => {
  const { inviteUser } = useInviteAuth();
  const { user, openProfileModal } = useAuth();
  const history = useHistory();
  const currentUser = inviteUser || (user ? { name: user.name, email: user.email } : null);
  const [activeTab, setActiveTab] = useState('included');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isChatOpen, setIsChatOpen] = useState(true);
  const [activeFaq, setActiveFaq] = useState<number | null>(null);

  // AI Generator Demo state
  const [aiEventType, setAiEventType] = useState('marriage');
  const [aiTone, setAiTone] = useState('warm');
  const [aiGeneratedText, setAiGeneratedText] = useState(
    "💖 With hearts full of love, you are cordially invited to celebrate the union of Raj & Priya. Join us as we start our forever journey! 💒✨ RSVP: [Link]"
  );
  const [isGenerating, setIsGenerating] = useState(false);

  // Redirect to dashboard if logged in
  useEffect(() => {
    if (inviteUser) {
      history.push('/invite/dashboard');
    }
  }, [inviteUser, history]);

  // Handle AI generation simulation
  const handleGenerateAI = () => {
    setIsGenerating(true);
    setTimeout(() => {
      let inviteText = '';
      if (aiEventType === 'marriage') {
        if (aiTone === 'formal') {
          inviteText = "✨ Mr. & Mrs. Sharma request the honor of your presence at the marriage reception of their children, Raj and Priya. Join us for a formal evening of celebration. RSVP: [Link]";
        } else if (aiTone === 'poetic') {
          inviteText = "🌹 Two souls, one heart, a journey about to start. Under the canopy of stars and vows, Raj & Priya invite you to witness their eternal beginning. RSVP: [Link]";
        } else {
          inviteText = "💖 With hearts full of love, you are cordially invited to celebrate the union of Raj & Priya. Join us as we start our forever journey! 💒✨ RSVP: [Link]";
        }
      } else if (aiEventType === 'birthday') {
        if (aiTone === 'casual') {
          inviteText = "🎉 Level up! I'm turning another year older and want to celebrate with my favorite people. Food, drinks, and bad dancing guaranteed. Show up! RSVP: [Link]";
        } else {
          inviteText = "🎂 Cheers to another year of laughter and memories! You are warmly invited to celebrate Priya's birthday party. Join us for a fun-filled evening. RSVP: [Link]";
        }
      } else {
        inviteText = "🏠 New house, new memories, same old friends! We've finally unpacked, and we want you to grace our new home. Join us for our Housewarming ceremony. RSVP: [Link]";
      }
      setAiGeneratedText(inviteText);
      setIsGenerating(false);
    }, 800);
  };

  const toggleFaq = (index: number) => {
    setActiveFaq(activeFaq === index ? null : index);
  };

  const scrollToTabs = (tabId: string) => {
    setActiveTab(tabId);
    const element = document.getElementById('features-explorer');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="workspace-landing">
      {/* NAVBAR */}
      <header className="workspace-navbar">
        <div className="workspace-navbar-left">
          <Link to="/invite" className="workspace-logo-container">
            <span className="workspace-logo-icon">✉️</span>
            <div className="workspace-logo-text">
              Invite<span>Studio</span>
            </div>
          </Link>
          <ul className="workspace-nav-links">
            <li className="workspace-nav-link" onClick={() => scrollToTabs('included')}>Solutions</li>
            <li className="workspace-nav-link" onClick={() => scrollToTabs('channels')}>Channels</li>
            <li className="workspace-nav-link" onClick={() => scrollToTabs('templates')}>Templates</li>
            <li className="workspace-nav-link" onClick={() => scrollToTabs('ai')}>AI Writer</li>
            <li className="workspace-nav-link" onClick={() => scrollToTabs('pricing')}>Pricing</li>
            <li className="workspace-nav-link" onClick={() => scrollToTabs('faq')}>FAQ</li>
          </ul>
        </div>

        <div className="workspace-navbar-right">
          {isSearchOpen ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', border: '1px solid #dadce0', borderRadius: '20px', padding: '2px 10px', background: '#f1f3f4' }}>
              <Search size={16} style={{ color: '#5f6368' }} />
              <input 
                type="text" 
                placeholder="Search..." 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ border: 'none', background: 'transparent', outline: 'none', padding: '4px', fontSize: '0.85rem', width: '120px' }} 
              />
              <button onClick={() => setIsSearchOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                <X size={14} style={{ color: '#5f6368' }} />
              </button>
            </div>
          ) : (
            <button className="workspace-search-btn" onClick={() => setIsSearchOpen(true)} title="Search">
              <Search size={20} />
            </button>
          )}
          
          {currentUser ? (
            <button 
              type="button" 
              onClick={openProfileModal}
              className="workspace-link-btn"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                cursor: 'pointer',
                fontWeight: 700
              }}
              title="Click to view profile"
            >
              <div style={{
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                background: '#8B5CF6',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.75rem',
                fontWeight: 800
              }}>
                {currentUser.name.charAt(0).toUpperCase()}
              </div>
              <span>{currentUser.name.split(' ')[0]}</span>
            </button>
          ) : (
            <Link to="/invite/login" className="workspace-link-btn">Log In</Link>
          )}
          <Link to="/invite/new" className="workspace-outline-btn">Create Invite</Link>
          {!currentUser && (
            <Link to="/invite/register" className="workspace-primary-btn">Start Free Trial</Link>
          )}
        </div>
      </header>

      {/* HERO SECTION */}
      <section className="workspace-hero-section">
        <div className="workspace-hero-grid">
          <div className="workspace-hero-left">
            <div className="workspace-product-icons">
              <div className="workspace-icon-pill" title="WhatsApp Delivery" style={{ color: '#25d366' }}>🟢</div>
              <div className="workspace-icon-pill" title="SMS Campaigns" style={{ color: '#1a73e8' }}>💬</div>
              <div className="workspace-icon-pill" title="Voice/IVR Robocalls" style={{ color: '#fbbc05' }}>📞</div>
              <div className="workspace-icon-pill" title="Telegram Bot Integration" style={{ color: '#0088cc' }}>✈️</div>
              <div className="workspace-icon-pill" title="Custom Email Templates" style={{ color: '#ea4335' }}>✉️</div>
              <div className="workspace-icon-pill" title="AI Text Generation" style={{ color: '#a142f4' }}>✨</div>
            </div>
            <h1>Personalized invite solutions</h1>
            <p>
              Use Invite Studio for RSVP campaigns, customized digital wedding cards, event websites, and automated omnichannel dispatch. Get all the tools your family needs to collaborate and get RSVPs done in one place.
            </p>
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
              <Link to="/invite/register" className="workspace-primary-btn" style={{ padding: '0.8rem 2.2rem', fontSize: '1rem', borderRadius: '24px' }}>
                Start Free Trial
              </Link>
              <Link to="/invite/new" className="workspace-outline-btn" style={{ padding: '0.8rem 2rem', fontSize: '1rem', borderRadius: '24px' }}>
                Create Custom Invite
              </Link>
            </div>
          </div>

          <div className="workspace-hero-right">
            <div className="workspace-visual-container">
              {/* Star graphic */}
              <div className="workspace-star-decoration"><Star fill="#1a73e8" size={32} /></div>
              
              {/* Central Text Invite Card - Mockup of the editor */}
              <div className="workspace-main-visual-card" style={{ backgroundImage: `url(${weddingBg})` }}>
                <div className="workspace-video-badge" title="Live Interactive Card Editor" style={{ backgroundColor: 'var(--workspace-purple)' }}>
                  <Sparkles size={14} fill="white" />
                </div>
                <div className="event-overlay-badge">💒 Raj & Priya Reception</div>
              </div>

              {/* Floating Icons */}
              <div className="workspace-floating-app-icon whatsapp" title="WhatsApp Campaigns">🟢</div>
              <div className="workspace-floating-app-icon mail" title="Personalized Email Templates">✉️</div>
              <div className="workspace-floating-app-icon meet" title="Interactive Audio / Voice calls">📞</div>

              {/* Floating Summary Card */}
              <div className="workspace-floating-summary">
                <div className="workspace-floating-summary-header">
                  <Sparkles size={14} style={{ color: '#fbbc05' }} />
                  <span>RSVP Summary</span>
                </div>
                <div className="workspace-floating-summary-stats">
                  <div className="workspace-stat-bar-container">
                    <span>Yes</span>
                    <div className="workspace-stat-bar">
                      <div className="workspace-stat-fill" style={{ width: '72%', backgroundColor: '#34a853' }}></div>
                    </div>
                    <span>72%</span>
                  </div>
                  <div className="workspace-stat-bar-container">
                    <span>No</span>
                    <div className="workspace-stat-bar">
                      <div className="workspace-stat-fill" style={{ width: '8%', backgroundColor: '#ea4335' }}></div>
                    </div>
                    <span>8%</span>
                  </div>
                  <div className="workspace-stat-bar-container">
                    <span>Maybe</span>
                    <div className="workspace-stat-bar">
                      <div className="workspace-stat-fill" style={{ width: '20%', backgroundColor: '#fbbc05' }}></div>
                    </div>
                    <span>20%</span>
                  </div>
                </div>
                <div className="workspace-skeleton-line short"></div>
              </div>

              {/* Floating Privacy Card */}
              <div className="workspace-floating-privacy">
                <div className="workspace-privacy-icon">
                  <ShieldCheck size={18} />
                </div>
                <div className="workspace-privacy-text">
                  <h5>Secure Guest Privacy</h5>
                  <p>Guest contact details are kept safe, encrypted, and never shared.</p>
                </div>
              </div>

              {/* Cursor Overlay - Pri Ghanda */}
              <div className="workspace-cursor-overlay pri">
                <div className="workspace-cursor-pointer"></div>
                <span>Pri Ghanda</span>
              </div>

              {/* Cursor Overlay - Madison Wells */}
              <div className="workspace-cursor-overlay madison">
                <div className="workspace-cursor-pointer"></div>
                <span>Madison Wells</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SUB NAVIGATION TAB BAR SECTION */}
      <section className="workspace-tabs-section" id="features-explorer">
        <div className="workspace-tabs-container">
          <div className="workspace-tab-nav">
            <button 
              className={`workspace-tab-btn ${activeTab === 'included' ? 'active' : ''}`}
              onClick={() => setActiveTab('included')}
            >
              What's included
            </button>
            <button 
              className={`workspace-tab-btn ${activeTab === 'channels' ? 'active' : ''}`}
              onClick={() => setActiveTab('channels')}
            >
              Delivery Channels
            </button>
            <button 
              className={`workspace-tab-btn ${activeTab === 'templates' ? 'active' : ''}`}
              onClick={() => setActiveTab('templates')}
            >
              Templates
            </button>
            <button 
              className={`workspace-tab-btn ${activeTab === 'ai' ? 'active' : ''}`}
              onClick={() => setActiveTab('ai')}
            >
              AI Assistant
            </button>
            <button 
              className={`workspace-tab-btn ${activeTab === 'pricing' ? 'active' : ''}`}
              onClick={() => setActiveTab('pricing')}
            >
              Pricing
            </button>
            <button 
              className={`workspace-tab-btn ${activeTab === 'faq' ? 'active' : ''}`}
              onClick={() => setActiveTab('faq')}
            >
              FAQ
            </button>
          </div>

          <div className="workspace-tab-content-panel">
            {/* INCLUDED FEATURES PANEL */}
            {activeTab === 'included' && (
              <div className="workspace-features-grid">
                <div className="workspace-feature-card">
                  <div className="workspace-feature-icon" style={{ backgroundColor: 'rgba(26,115,232,0.1)', color: '#1a73e8' }}>
                    <Compass size={24} />
                  </div>
                  <h4>Omnichannel Dispatch</h4>
                  <p>Build your guest campaign batches and send invites over WhatsApp, SMS, Telegram, or automated interactive voice response (IVR) calls.</p>
                </div>
                <div className="workspace-feature-card">
                  <div className="workspace-feature-icon" style={{ backgroundColor: 'rgba(52,168,83,0.1)', color: '#34a853' }}>
                    <Calendar size={24} />
                  </div>
                  <h4>Automated RSVPs</h4>
                  <p>Guests click personalized links to RSVP, which instantly updates your dashboard stats, food count, and seating plan in real-time.</p>
                </div>
                <div className="workspace-feature-card">
                  <div className="workspace-feature-icon" style={{ backgroundColor: 'rgba(161,66,244,0.1)', color: '#a142f4' }}>
                    <Users size={24} />
                  </div>
                  <h4>Collaborative Workspace</h4>
                  <p>Invite family members, wedding planners, or friends to collaborate, edit guest lists, and review customized invitation templates.</p>
                </div>
              </div>
            )}

            {/* DELIVERY CHANNELS PANEL */}
            {activeTab === 'channels' && (
              <div className="workspace-channels-grid">
                <div className="workspace-channel-box">
                  <div className="workspace-channel-avatar" style={{ backgroundColor: '#e8f5e9', color: '#25d366' }}>🟢</div>
                  <div className="workspace-channel-details">
                    <h4>WhatsApp Invitation</h4>
                    <p>Deliver rich multimedia cards and instant RSVP buttons straight to your guest's chats. Ideal for weddings and birthdays.</p>
                    <div className="workspace-channel-meta">
                      <span className="workspace-meta-tag green">98% Open Rate</span>
                      <span className="workspace-meta-tag">Fast RSVP Responses</span>
                    </div>
                  </div>
                </div>
                <div className="workspace-channel-box">
                  <div className="workspace-channel-avatar" style={{ backgroundColor: '#e8f0fe', color: '#1a73e8' }}>💬</div>
                  <div className="workspace-channel-details">
                    <h4>SMS & Smart Links</h4>
                    <p>Reach any mobile phone directly. Delivers clean SMS invitation text with custom click-through smart links for fast mobile RSVPs.</p>
                    <div className="workspace-channel-meta">
                      <span className="workspace-meta-tag">Direct SMS Gateway</span>
                      <span className="workspace-meta-tag">100% Mobile Coverage</span>
                    </div>
                  </div>
                </div>
                <div className="workspace-channel-box">
                  <div className="workspace-channel-avatar" style={{ backgroundColor: '#fef7e0', color: '#fbbc05' }}>📞</div>
                  <div className="workspace-channel-details">
                    <h4>IVR Robocalls (Voice Alerts)</h4>
                    <p>Automated warm telephone calls that play your recorded message or synthesise AI speech. Guests RSVP by pressing keys (e.g. Press 1 for Yes).</p>
                    <div className="workspace-channel-meta">
                      <span className="workspace-meta-tag">Interactive Voice Responses</span>
                      <span className="workspace-meta-tag">Elderly Friendly</span>
                    </div>
                  </div>
                </div>
                <div className="workspace-channel-box">
                  <div className="workspace-channel-avatar" style={{ backgroundColor: '#f3e8fd', color: '#a142f4' }}>✈️</div>
                  <div className="workspace-channel-details">
                    <h4>Telegram Bots & Email</h4>
                    <p>Dispatch details through custom Telegram interactive bots or design stunning HTML emails for professional newsletters or events.</p>
                    <div className="workspace-channel-meta">
                      <span className="workspace-meta-tag">Telegram Gateway</span>
                      <span className="workspace-meta-tag">Rich Email Templates</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* TEMPLATE GALLERY PANEL */}
            {activeTab === 'templates' && (
              <div className="workspace-templates-grid">
                <div className="workspace-template-card">
                  <div className="workspace-template-preview" style={{ backgroundImage: `url(${weddingBg})` }}>
                    <span className="workspace-template-badge">Wedding</span>
                  </div>
                  <div className="workspace-template-info">
                    <h5>Royal Gold Palace</h5>
                    <p>Luxurious traditional style</p>
                  </div>
                </div>
                <div className="workspace-template-card">
                  <div className="workspace-template-preview" style={{ backgroundColor: '#fce4ec', backgroundImage: 'radial-gradient(circle, #f8bbd0 10%, transparent 11%)', backgroundSize: '15px 15px' }}>
                    <span className="workspace-template-badge" style={{ backgroundColor: '#ea4335', color: 'white' }}>Birthday</span>
                  </div>
                  <div className="workspace-template-info">
                    <h5>Joyful Confetti</h5>
                    <p>Lively, bright & playful</p>
                  </div>
                </div>
                <div className="workspace-template-card">
                  <div className="workspace-template-preview" style={{ backgroundColor: '#e0f2f1', backgroundImage: 'linear-gradient(45deg, #b2dfdb 25%, transparent 25%)', backgroundSize: '20px 20px' }}>
                    <span className="workspace-template-badge" style={{ backgroundColor: '#34a853', color: 'white' }}>Housewarming</span>
                  </div>
                  <div className="workspace-template-info">
                    <h5>Sweet Home Cozy</h5>
                    <p>Minimalist, botanical layout</p>
                  </div>
                </div>
                <div className="workspace-template-card">
                  <div className="workspace-template-preview" style={{ backgroundColor: '#eaeaea', backgroundImage: 'linear-gradient(to right, #ccc 1px, transparent 1px)', backgroundSize: '30px 30px' }}>
                    <span className="workspace-template-badge" style={{ backgroundColor: '#202124', color: 'white' }}>Corporate</span>
                  </div>
                  <div className="workspace-template-info">
                    <h5>Tech Summit 2026</h5>
                    <p>Clean, professional, modern</p>
                  </div>
                </div>
              </div>
            )}

            {/* AI ASSISTANT PANEL */}
            {activeTab === 'ai' && (
              <div className="workspace-ai-section">
                <div>
                  <h3 style={{ fontSize: '1.75rem', fontWeight: 700, margin: '0 0 1rem 0' }}>AI-Powered Smart Content</h3>
                  <p style={{ color: '#5f6368', lineHeight: 1.6, fontSize: '1.05rem', margin: '0 0 2rem 0' }}>
                    Generate beautiful invitation texts, event headlines, or WhatsApp messages using our built-in AI assistant. Select the occasion, set your preferred tone, and let our engine draft the perfect invitation wording.
                  </p>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <li style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.95rem', color: '#202124', fontWeight: 500 }}>
                      <span style={{ color: '#34a853', fontWeight: 'bold' }}>✓</span> Support for English, Hindi, Punjabi, Bengali, and Spanish.
                    </li>
                    <li style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.95rem', color: '#202124', fontWeight: 500 }}>
                      <span style={{ color: '#34a853', fontWeight: 'bold' }}>✓</span> Tone selector (Formal, Humorous, Poetic, Warm, Casual).
                    </li>
                    <li style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.95rem', color: '#202124', fontWeight: 500 }}>
                      <span style={{ color: '#34a853', fontWeight: 'bold' }}>✓</span> Instant template copy & direct integration in wizard.
                    </li>
                  </ul>
                </div>

                <div className="workspace-ai-demo">
                  <div className="workspace-ai-demo-header">
                    <span className="workspace-ai-demo-spark">✨</span>
                    <h5>AI Writer Assistant Demo</h5>
                  </div>
                  <div className="workspace-ai-demo-form">
                    <div className="workspace-ai-input-group">
                      <label>Event Type</label>
                      <select 
                        className="workspace-ai-select" 
                        value={aiEventType}
                        onChange={(e) => setAiEventType(e.target.value)}
                      >
                        <option value="marriage">Wedding Reception</option>
                        <option value="birthday">Birthday Party</option>
                        <option value="housewarming">Housewarming Ceremony</option>
                      </select>
                    </div>

                    <div className="workspace-ai-input-group">
                      <label>Tone of Voice</label>
                      <select 
                        className="workspace-ai-select" 
                        value={aiTone}
                        onChange={(e) => setAiTone(e.target.value)}
                      >
                        <option value="warm">Warm & Welcoming</option>
                        <option value="formal">Formal & Elegant</option>
                        <option value="casual">Casual & Fun</option>
                        <option value="poetic">Poetic & Artistic</option>
                      </select>
                    </div>

                    <button 
                      onClick={handleGenerateAI} 
                      className="workspace-primary-btn" 
                      style={{ background: 'linear-gradient(135deg, #a142f4 0%, #1a73e8 100%)', border: 'none', borderRadius: '8px', marginTop: '4px' }}
                      disabled={isGenerating}
                    >
                      {isGenerating ? 'Writing...' : '✨ Generate Invitation Text'}
                    </button>

                    <div className="workspace-ai-output">
                      {isGenerating ? (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                          <span style={{ animation: 'spin 1s linear infinite' }}>⏳</span> Generating custom draft...
                        </div>
                      ) : (
                        aiGeneratedText
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* PRICING PANEL */}
            {activeTab === 'pricing' && (
              <div className="workspace-pricing-grid">
                <div className="workspace-pricing-card">
                  <div className="workspace-pricing-header">
                    <h4>Basic Free</h4>
                    <div className="workspace-pricing-price">
                      <span className="workspace-price-amount">₹0</span>
                      <span className="workspace-price-period"> / event</span>
                    </div>
                  </div>
                  <ul className="workspace-pricing-features">
                    <li><span>✓</span> Send up to 10 guest invitations</li>
                    <li><span>✓</span> Standard Web page RSVP template</li>
                    <li><span>✓</span> Instant digital preview</li>
                    <li><span>✓</span> Ad-supported guest experience</li>
                  </ul>
                  <Link to="/invite/new" className="workspace-outline-btn" style={{ textAlign: 'center', marginTop: '1.5rem' }}>
                    Start Free
                  </Link>
                </div>

                <div className="workspace-pricing-card popular">
                  <span className="workspace-popular-badge">Best Choice</span>
                  <div className="workspace-pricing-header">
                    <h4>Standard Plan</h4>
                    <div className="workspace-pricing-price">
                      <span className="workspace-price-amount">₹999</span>
                      <span className="workspace-price-period"> / event</span>
                    </div>
                  </div>
                  <ul className="workspace-pricing-features">
                    <li><span>✓</span> Send up to 150 guest invitations</li>
                    <li><span>✓</span> Ad-free custom web card layout</li>
                    <li><span>✓</span> WhatsApp & Telegram dispatch</li>
                    <li><span>✓</span> PDF invite generator</li>
                    <li><span>✓</span> Real-time RSVP analytics dashboard</li>
                  </ul>
                  <Link to="/invite/register?plan=standard" className="workspace-primary-btn" style={{ textAlign: 'center', marginTop: '1.5rem' }}>
                    Choose Standard
                  </Link>
                </div>

                <div className="workspace-pricing-card">
                  <div className="workspace-pricing-header">
                    <h4>Premium Pro</h4>
                    <div className="workspace-pricing-price">
                      <span className="workspace-price-amount">₹2,499</span>
                      <span className="workspace-price-period"> / event</span>
                    </div>
                  </div>
                  <ul className="workspace-pricing-features">
                    <li><span>✓</span> Send up to 1000 guest invitations</li>
                    <li><span>✓</span> Custom HTML theme templates</li>
                    <li><span>✓</span> Omnichannel (WhatsApp & Telegram)</li>
                    <li><span>✓</span> Photo & Video Delivery (WhatsApp & Telegram)</li>
                    <li><span>✓</span> AI Writer assistant fully unlocked</li>
                    <li><span>✓</span> Premium support & RSVP reminders</li>
                  </ul>
                  <Link to="/invite/register?plan=premium" className="workspace-outline-btn" style={{ textAlign: 'center', marginTop: '1.5rem', borderColor: 'var(--workspace-text)' }}>
                    Get Premium Pro
                  </Link>
                </div>
              </div>
            )}

            {/* FAQ ACCORDION PANEL */}
            {activeTab === 'faq' && (
              <div className="workspace-faq-container">
                <div className="workspace-faq-item">
                  <button className="workspace-faq-question" onClick={() => toggleFaq(0)}>
                    How do delivery channels work in Invite Studio?
                    <span>{activeFaq === 0 ? <ChevronDown size={18} /> : <ChevronRight size={18} />}</span>
                  </button>
                  {activeFaq === 0 && (
                    <div className="workspace-faq-answer">
                      Invite Studio supports multiple dispatch routes. You can upload an Excel guest spreadsheet, design the template text, and select WhatsApp, SMS, Telegram, or automated Voice Calls. Our backend relays the personalized messages directly to the recipients.
                    </div>
                  )}
                </div>
                <div className="workspace-faq-item">
                  <button className="workspace-faq-question" onClick={() => toggleFaq(1)}>
                    Is there a limit on free guest invitations?
                    <span>{activeFaq === 1 ? <ChevronDown size={18} /> : <ChevronRight size={18} />}</span>
                  </button>
                  {activeFaq === 1 && (
                    <div className="workspace-faq-answer">
                      Yes. The Basic Free Account allows you to create events and send up to 10 free email invitations. To invite more guests and unlock premium channels like WhatsApp or IVR voice, you can purchase an event upgrade package.
                    </div>
                  )}
                </div>
                <div className="workspace-faq-item">
                  <button className="workspace-faq-question" onClick={() => toggleFaq(2)}>
                    How do guest responses update the seating dashboard?
                    <span>{activeFaq === 2 ? <ChevronDown size={18} /> : <ChevronRight size={18} />}</span>
                  </button>
                  {activeFaq === 2 && (
                    <div className="workspace-faq-answer">
                      Every invitation contains a unique smart link. When a guest clicks the link, they visit an elegant RSVP page where they specify attendance (Yes/No/Maybe), food preferences, and extra guest counts. These answers immediately populate your campaign analytics table in the dashboard.
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CHATBOT PILL & FLOATING BUBBLE */}
      <div className="workspace-chatbot-container">
        {isChatOpen && (
          <div className="workspace-chatbot-bubble">
            <p className="workspace-chatbot-text">
              Hi there 👋 What brings you to Invite Studio today?
            </p>
            <button className="workspace-chatbot-close" onClick={() => setIsChatOpen(false)}>
              <X size={14} />
            </button>
          </div>
        )}
        <button className="workspace-chatbot-btn" onClick={() => setIsChatOpen(!isChatOpen)}>
          <MessageCircle size={24} />
        </button>
      </div>

      {/* FOOTER */}
      <footer className="workspace-footer">
        <div className="workspace-footer-links">
          <a href="#" className="workspace-footer-link">Privacy Policy</a>
          <a href="#" className="workspace-footer-link">Terms of Service</a>
          <a href="#" className="workspace-footer-link">Contact Support</a>
          <a href="#" className="workspace-footer-link">Developer API</a>
        </div>
        <p>© 2026 Shadigram Workspace. All rights reserved. E-Invitations made simple.</p>
      </footer>
    </div>
  );
};

export default InviteLandingPage;
